<script lang="ts">
  import { flip } from "svelte/animate";
  import { fade } from "svelte/transition";
  import { sceneGradient, type HueCatalogDto, type HuePin, type HueSceneDto } from "@piano/shared";
  import { api } from "../../lib/api/index.js";
  import { hue } from "../../lib/stores/hue.svelte.js";
  import { ui } from "../../lib/stores/ui.svelte.js";
  import Icon from "../Icon.svelte";
  import Sheet from "../Sheet.svelte";
  import SceneTile from "./SceneTile.svelte";

  /** Auswahl für den Notenständer: Szenen und Lampen nach Raum anheften, sortieren, ausprobieren. */
  let { onclose }: { onclose: () => void } = $props();

  let catalog = $state<HueCatalogDto | null>(null);
  let pins = $state<HuePin[]>(hue.status?.pins ?? []);
  let groupId = $state<string | null>(null);
  let failed = $state<string | null>(null);

  void load();

  async function load(): Promise<void> {
    try {
      catalog = await api.hueCatalog();
      pins = hue.status?.pins ?? [];
      groupId = defaultGroup(catalog);
    } catch (error) {
      failed = error instanceof Error ? error.message : "Bridge nicht erreichbar";
    }
  }

  /** Raum mit den meisten angehefteten Szenen, sonst Wohnzimmer, sonst der erste Raum. */
  function defaultGroup(c: HueCatalogDto): string | null {
    const counts = new Map<string, number>();
    for (const pin of pins) {
      const scene = c.scenes.find((s) => s.id === pin.id);
      if (scene) counts.set(scene.groupId, (counts.get(scene.groupId) ?? 0) + 1);
    }
    const best = [...counts.entries()].sort((a, b) => b[1] - a[1])[0]?.[0];
    return best ?? c.groups.find((g) => /wohn|living/i.test(g.name))?.id ?? c.groups[0]?.id ?? null;
  }

  const scenes = $derived(catalog?.scenes.filter((s) => s.groupId === groupId) ?? []);
  const lights = $derived(catalog?.lights.filter((l) => l.roomId === groupId) ?? []);
  const isPinned = (type: HuePin["type"], id: string) => pins.some((p) => p.type === type && p.id === id);
  const pinCount = (gid: string) =>
    pins.filter((p) => catalog?.scenes.find((s) => s.id === p.id)?.groupId === gid).length;

  function toggle(type: HuePin["type"], id: string): void {
    pins = isPinned(type, id)
      ? pins.filter((p) => !(p.type === type && p.id === id))
      : [...pins, { type, id }];
    save();
  }

  let saveTimer: ReturnType<typeof setTimeout> | undefined;
  function save(): void {
    clearTimeout(saveTimer);
    saveTimer = setTimeout(async () => {
      try {
        hue.status = await api.huePins(pins);
        void hue.loadDock();
      } catch (error) {
        ui.error(error);
      }
    }, 350);
  }

  async function tryScene(scene: HueSceneDto): Promise<void> {
    try {
      await api.hueRecall(scene.id);
      ui.toast(`„${scene.name}“ ist an`, "success");
    } catch (error) {
      ui.error(error, "Bridge nicht erreichbar");
    }
  }

  // --- Sortieren der angehefteten Elemente (wie in der Setlist) ---------------------
  const ROW = 56;
  let drag = $state<{ key: string; from: number; dy: number } | null>(null);
  let startY = 0;
  const keyOf = (p: HuePin) => `${p.type}:${p.id}`;
  const displayed = $derived.by(() => {
    if (!drag) return pins;
    const to = Math.max(0, Math.min(pins.length - 1, drag.from + Math.round(drag.dy / ROW)));
    const moving = pins[drag.from]!;
    const next = pins.filter((_, i) => i !== drag!.from);
    next.splice(to, 0, moving);
    return next;
  });
  function dragStart(event: PointerEvent, pin: HuePin): void {
    (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
    startY = event.clientY;
    drag = { key: keyOf(pin), from: pins.findIndex((p) => keyOf(p) === keyOf(pin)), dy: 0 };
  }
  function dragMove(event: PointerEvent): void {
    if (drag) drag = { ...drag, dy: event.clientY - startY };
  }
  function dragEnd(): void {
    if (!drag) return;
    const changed = displayed.some((p, i) => keyOf(p) !== keyOf(pins[i]!));
    pins = displayed;
    drag = null;
    if (changed) save();
  }
  function dragOffset(pin: HuePin): number {
    if (!drag || drag.key !== keyOf(pin)) return 0;
    return drag.from * ROW + drag.dy - displayed.findIndex((p) => keyOf(p) === drag!.key) * ROW;
  }

  function describe(pin: HuePin): { name: string; sub: string; background: string } {
    if (pin.type === "scene") {
      const scene = catalog?.scenes.find((s) => s.id === pin.id);
      const room = catalog?.groups.find((g) => g.id === scene?.groupId);
      return {
        name: scene?.name ?? "Szene",
        sub: `Szene · ${room?.name ?? ""}`,
        background: sceneGradient(scene?.colors ?? []),
      };
    }
    const light = catalog?.lights.find((l) => l.id === pin.id);
    const room = catalog?.groups.find((g) => g.id === light?.roomId);
    return {
      name: light?.name ?? "Lampe",
      sub: `Lampe${room ? ` · ${room.name}` : ""}`,
      background: "var(--raised-2)",
    };
  }
</script>

<Sheet title="Licht am Notenständer" {onclose} width={620}>
  <div class="picker">
    <header>
      <h2>Licht am Notenständer</h2>
      <p class="t2">Was du hier anheftest, erscheint im Viewer unter „Licht“.</p>
    </header>

    {#if failed}
      <p class="problem">{failed}</p>
    {:else if !catalog}
      <div class="loading" aria-busy="true"><span></span><span></span><span></span></div>
    {:else}
      <section>
        <span class="label">Im Notenständer · {pins.length}</span>
        {#if pins.length}
          <ul class="pinned" style:height="{pins.length * ROW}px">
            {#each displayed as pin (keyOf(pin))}
              {@const info = describe(pin)}
              <li
                class:dragging={drag?.key === keyOf(pin)}
                style:transform={drag?.key === keyOf(pin)
                  ? `translateY(${dragOffset(pin)}px) scale(1.02)`
                  : null}
                animate:flip={{ duration: drag?.key === keyOf(pin) ? 0 : 260 }}
              >
                <span class="mini" style:background={info.background}
                  >{#if pin.type === "light"}<Icon name="bulb" size={16} />{/if}</span
                >
                <span class="grow"
                  ><strong class="ellipsis">{info.name}</strong><small class="t3">{info.sub}</small></span
                >
                <button
                  type="button"
                  class="icon-btn small"
                  aria-label="{info.name} entfernen"
                  onclick={() => toggle(pin.type, pin.id)}><Icon name="close" size={16} /></button
                >
                <span
                  class="grip"
                  role="button"
                  tabindex="-1"
                  aria-label="Verschieben"
                  onpointerdown={(e) => dragStart(e, pin)}
                  onpointermove={dragMove}
                  onpointerup={dragEnd}
                  onpointercancel={dragEnd}><Icon name="grip" size={18} /></span
                >
              </li>
            {/each}
          </ul>
        {:else}
          <p class="t3 empty">Noch nichts angeheftet – tippe unten auf Szenen oder Lampen.</p>
        {/if}
      </section>

      <div class="tabs" role="tablist" aria-label="Räume">
        {#each catalog.groups as g (g.id)}
          <button
            type="button"
            role="tab"
            class="tab"
            aria-selected={g.id === groupId}
            onclick={() => (groupId = g.id)}
          >
            {g.name}{#if pinCount(g.id)}<span class="count">{pinCount(g.id)}</span>{/if}
          </button>
        {/each}
      </div>

      {#key groupId}
        <div class="room" in:fade={{ duration: 180 }}>
          <section>
            <span class="label">Szenen</span>
            {#if scenes.length}
              <div class="grid">
                {#each scenes as scene (scene.id)}
                  <div class="cell">
                    <SceneTile
                      {scene}
                      pinned={isPinned("scene", scene.id)}
                      onclick={() => toggle("scene", scene.id)}
                    />
                    <button
                      type="button"
                      class="try"
                      aria-label="„{scene.name}“ ausprobieren"
                      onclick={() => tryScene(scene)}><Icon name="play" size={14} /></button
                    >
                  </div>
                {/each}
              </div>
            {:else}
              <p class="t3 empty">In diesem Raum gibt es keine Szenen.</p>
            {/if}
          </section>

          {#if lights.length}
            <section>
              <span class="label">Lampen</span>
              <div class="lamps">
                {#each lights as light (light.id)}
                  <button
                    type="button"
                    class="lamp"
                    role="switch"
                    aria-checked={isPinned("light", light.id)}
                    onclick={() => toggle("light", light.id)}
                  >
                    <span class="status" style:background={light.on ? (light.color ?? "#FFD9A8") : null}
                    ></span>
                    <span class="grow"
                      ><strong>{light.name}</strong><small class="t3"
                        >{light.on ? `an · ${light.brightness ?? 100} %` : "aus"}</small
                      ></span
                    >
                    <span class="t3 in-dock">Im Dock</span>
                    <span class="switch" aria-checked={isPinned("light", light.id)}></span>
                  </button>
                {/each}
              </div>
            </section>
          {/if}
        </div>
      {/key}
    {/if}

    <div class="actions"><button type="button" class="btn btn-primary" onclick={onclose}>Fertig</button></div>
  </div>
</Sheet>

<style>
  .picker {
    display: flex;
    flex-direction: column;
    gap: 18px;
    padding: 0 8px 8px;
  }
  header {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  h2 {
    font-size: 22px;
  }
  p {
    margin: 0;
  }
  section {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  .label {
    padding: 0 4px;
  }
  .empty {
    padding: 4px;
    font-size: 14px;
  }
  .problem {
    padding: 10px 14px;
    border-radius: var(--r-md);
    background: rgba(255, 122, 89, 0.12);
    color: var(--accent-2);
    font-weight: 600;
  }
  .pinned {
    position: relative;
    list-style: none;
    margin: 0;
    padding: 0;
  }
  .pinned li {
    height: 56px;
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 0 4px 0 8px;
    border-radius: var(--r-md);
    background: var(--surface-2);
    margin-bottom: 0;
    box-shadow: inset 0 -1px 0 var(--line);
    transition: box-shadow var(--dur-fast);
  }
  .pinned li.dragging {
    position: relative;
    z-index: 2;
    background: var(--raised);
    box-shadow: var(--shadow-1);
  }
  .mini {
    width: 40px;
    height: 32px;
    flex: 0 0 auto;
    border-radius: var(--r-sm);
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--text-2);
  }
  .grow {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 1px;
    text-align: left;
  }
  strong {
    font-size: 15px;
  }
  small {
    font-size: 12px;
    font-weight: 600;
  }
  .grip {
    width: 40px;
    height: 44px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--text-3);
    cursor: grab;
    touch-action: none;
  }
  .tabs {
    display: flex;
    gap: 6px;
    overflow-x: auto;
    scrollbar-width: none;
    padding: 2px;
  }
  .tab {
    flex: 0 0 auto;
    height: 36px;
    padding: 0 14px;
    border-radius: var(--r-pill);
    background: var(--surface-2);
    color: var(--text-2);
    font-weight: 700;
    font-size: 14px;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    transition:
      background var(--dur-fast),
      color var(--dur-fast);
  }
  .tab[aria-selected="true"] {
    background: var(--text);
    color: var(--text-on-light);
  }
  .count {
    min-width: 18px;
    height: 18px;
    padding: 0 5px;
    border-radius: var(--r-pill);
    background: var(--accent);
    color: var(--on-accent);
    font-size: 11px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }
  .room {
    display: flex;
    flex-direction: column;
    gap: 18px;
  }
  .grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(132px, 1fr));
    gap: 8px;
  }
  .cell {
    position: relative;
  }
  .try {
    position: absolute;
    left: 14px;
    top: 14px;
    width: 28px;
    height: 28px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(0, 0, 0, 0.4);
    color: #fff;
    backdrop-filter: blur(6px);
    -webkit-backdrop-filter: blur(6px);
    transition: transform var(--dur-tap);
  }
  .try:active {
    transform: scale(0.88);
  }
  .lamps {
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding: 6px;
    border-radius: 18px;
    background: var(--surface-2);
  }
  .lamp {
    display: flex;
    align-items: center;
    gap: 12px;
    min-height: 54px;
    padding: 0 10px;
    border-radius: var(--r-md);
    transition: background var(--dur-tap);
  }
  .lamp:hover {
    background: rgba(255, 255, 255, 0.05);
  }
  .status {
    width: 12px;
    height: 12px;
    flex: 0 0 auto;
    border-radius: 50%;
    background: var(--raised-2);
  }
  .in-dock {
    font-size: 12px;
    font-weight: 700;
  }
  .loading {
    display: flex;
    gap: 8px;
    justify-content: center;
    padding: 40px 0;
  }
  .loading span {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: var(--text-3);
    animation: blink 1100ms infinite both;
  }
  .loading span:nth-child(2) {
    animation-delay: 160ms;
  }
  .loading span:nth-child(3) {
    animation-delay: 320ms;
  }
  @keyframes blink {
    50% {
      opacity: 0.25;
    }
  }
  .actions {
    display: flex;
    justify-content: flex-end;
  }
</style>
